/*
Structure of config:
{
    baseURL: string,
    endpoint: string, (usually '/search')
    hydrator: {
        request: (params: Object, criteria: Object) => Object,
        response: (data: Object) => Object,
    },
    request: {
        properties: Object,
        idProperty: String,
    },
}
*/

export default class JobboardService {
    #config;
    /** @type {(params: Object, criteria: Object) => Object} */
    #mapRequest;
    /** @type {(responseData: Object) => Object} */
    #mapResponse;

    constructor(config) {
        this.#config = config;
        this.#mapRequest = config.hydrator.request;
        this.#mapResponse = config.hydrator.response;
    }

    get #url() {
        const baseURL = this.#trimSlashes(this.#config.baseURL);
        const endpoint = this.#trimSlashes(this.#config.endpoint);
        // don't use 'baseURL' param of the URL constructor, because it will
        // omit all paths from baseURL
        return new URL(`${baseURL}/${endpoint}`);
    }

    /**
     * Retrieves information on a single job-ad with the given id.
     * @param {number} id
     * @param {Array<string>} properties the properties of the job-ad that should be fetched
     * @return {Promise<Object | undefined>}
     */
    async get(id, properties = undefined) {
        const url = this.#url;
        url.search = this.#prepareURLParams(
            {
                [this.#config.request.idProperty]: id,
            },
            properties,
        ).toString();

        const response = await fetch(url);
        const data = this.#mapResponse(await response.json());

        if (data.items[0]?.[this.#config.request.idProperty] !== id) {
            // could not find a job ad with the given id
            return undefined;
        }
        return data.items[0];
    }

    #prepareURLParams(searchCriteria, searchParams = undefined) {
        if (!searchParams) {
            searchParams = {
                properties: this.#config.request.properties,
            };
        }

        searchParams.properties = [
            ...searchParams.properties,
            this.#config.request.idProperty,
        ];

        const params = this.#mapRequest(searchParams, searchCriteria);
        const stringifiedParams = Object.entries(params).reduce(
            (obj, [key, value]) => {
                obj[key] = JSON.stringify(value);
                return obj;
            },
            {},
        );
        return new URLSearchParams(stringifiedParams);
    }

    #trimSlashes(path) {
        if (path.startsWith('/')) {
            path = path.substring(1);
        }
        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }
        return path;
    }
}
